/**
 * FlahaSOIL v2 — mock API client.
 *
 * Returns deterministic, typed mock objects from `@flaha/shared-types`
 * so Phase 5 pages can render realistic UI without a backend. Every
 * method satisfies `ApiV2Client`; switching to the real client in
 * Phase 6 is a one-line change at the import site.
 */
import { AccountStatus, type AcceptInvitationRequest,
	type AcceptInvitationResponse,
	type AuthLoginResponse,
	type AuthLogoutResponse,
	type AuthMeResponse,
	type AuthRefreshResponse,
	type AuthRegisterResponse,
	type AuthSessionDTO,
	type CalculateSoilTestRequest,
	type CalculateSoilTestResponse,
	type ChartSnapshotsDTO,
	type CreateInvitationRequest,
	type CreateInvitationResponse,
	type CreateProjectRequest,
	type CreateProjectResponse,
	type CreateSoilReportRequest,
	type CreateSoilReportResponse,
	type CreateSoilSampleRequest,
	type CreateSoilSampleResponse,
	type CreateSoilTestRequest,
	type CreateSoilTestResponse,
	type FlahaCalcExportResponse,
	type GenerateReportRequest,
	type GenerateReportResponse,
	type GetCurrentUserResponse,
	type GetOrganizationResponse,
	type GetProjectResponse,
	type GetReportResponse,
	type GetReportVersionResponse,
	type GetSoilInterpretationResponse,
	type GetSoilSampleResponse,
	type GetSoilTestReportResponse,
	type GetSoilTestReportSummaryResponse,
	type GetSoilTestResponse,
	type ListInvitationsResponse,
	type ListOrganizationMembersResponse,
	type ListProjectReportsResponse,
	type ListProjectsQuery,
	type ListProjectsResponse,
	type ListReportVersionsResponse,
	type LoginRequest,
	type OrganizationInvitationDTO,
	type OrganizationMemberDTO,
	type OrganizationMembershipDTO,
	type PatchMembershipRequest,
	type PatchMembershipResponse,
	type PatchOrganizationRequest,
	type PatchOrganizationResponse,
	type PatchReportRequest,
	type PatchReportResponse,
	type ProfessionalReportDTO,
	type ProjectDTO,
	type ProjectSummaryDTO,
	type RegenerateReportResponse,
	type RegisterRequest,
	type RemoveMembershipResponse,
	type ReportVersionDTO,
	type RevokeInvitationResponse,
	type ScientificAnalysisResponse,
	type SoilReportDTO,
	type SwitchOrganizationRequest,
	type SwitchOrganizationResponse,
	type UserMembershipsResponse,
	InvitationStatus,
	MembershipStatus,
	OrganizationRole,
	OrganizationStatus,
	OrganizationType,
	ProjectStatus,
	SoilInterpretationRating,
	SoilReportStatus,
	SoilTestLevel,
	SoilValueSource,
	UserRole,
} from "@flaha/shared-types";

import type { ApiV2Client } from "./apiV2Client";

const NOW = "2026-05-05T08:00:00.000Z";
const MOCK_SAMPLE_ID = "smpl_mock_001";
const MOCK_TEST_ID = "test_mock_001";
const MOCK_PROJECT_ID = "proj_mock_001";

// Phase 8B: the mock client mirrors the dev-session model — every
// fixture is owned by a single seeded user that matches the backend's
// `user_dev_admin`. The mock no longer accepts a user id from the
// caller; all ownership filtering uses MOCK_USER_ID internally.
const MOCK_USER_ID = "user_dev_admin";

// Phase 9A-G — mock auth fixtures. The mock client always pretends the
// caller is signed in as `user_dev_admin` and a member of one personal
// org. Auth methods return canned tokens so the SPA can be exercised
// end-to-end without a backend; `accessToken` is a sentinel value the
// real client would never accept.
const MOCK_ORG_ID = "org_dev_admin";
const MOCK_MEMBERSHIP_ID = "mem_dev_admin";
// Phase 9A-H — a second mock organization so the TenantSwitcher renders
// a non-trivial picker in mock mode. Mirrors the role-matrix demo set
// the backend seed provisions ("Flaha Demo Organization").
const MOCK_DEMO_ORG_ID = "org_flaha_demo_full";
const MOCK_DEMO_MEMBERSHIP_ID = "mem_dev_admin_demo";
const MOCK_ACCESS_TOKEN = "mock.access.token";
// Far future so the in-memory store never flags it as expired during
// a mock-only session.
const MOCK_ACCESS_TOKEN_EXPIRES_AT = "2099-12-31T23:59:59.000Z";

// Tracks which org the mock session currently considers active. Lives at
// module scope so a `switchOrganization` call followed by a `refresh`
// returns a consistent session, mirroring the backend's persistence on
// the User row.
let mockActiveOrganizationId: string = MOCK_ORG_ID;

function buildMockOrganizations(): {
	personal: ReturnType<typeof buildPersonalOrg>;
	demo: ReturnType<typeof buildDemoOrg>;
} {
	return { personal: buildPersonalOrg(), demo: buildDemoOrg() };
}

function buildPersonalOrg() {
	return {
		id: MOCK_ORG_ID,
		name: "Development User's workspace",
		slug: "dev-admin",
		type: OrganizationType.CONSULTANCY,
		status: OrganizationStatus.ACTIVE,
		createdAt: NOW,
		updatedAt: NOW,
	};
}

function buildDemoOrg() {
	return {
		id: MOCK_DEMO_ORG_ID,
		name: "Flaha Demo Organization",
		slug: "flaha-demo-org",
		type: OrganizationType.COMPANY,
		status: OrganizationStatus.ACTIVE,
		createdAt: NOW,
		updatedAt: NOW,
	};
}

function buildMockMemberships(): OrganizationMembershipDTO[] {
	const { personal, demo } = buildMockOrganizations();
	return [
		{
			id: MOCK_MEMBERSHIP_ID,
			organizationId: personal.id,
			userId: MOCK_USER_ID,
			role: OrganizationRole.OWNER,
			status: MembershipStatus.ACTIVE,
			invitedById: null,
			invitedAt: null,
			acceptedAt: NOW,
			createdAt: NOW,
			updatedAt: NOW,
			organization: personal,
		},
		{
			id: MOCK_DEMO_MEMBERSHIP_ID,
			organizationId: demo.id,
			userId: MOCK_USER_ID,
			role: OrganizationRole.AGRONOMIST,
			status: MembershipStatus.ACTIVE,
			invitedById: null,
			invitedAt: null,
			acceptedAt: NOW,
			createdAt: NOW,
			updatedAt: NOW,
			organization: demo,
		},
	];
}

function buildMockAuthSession(): AuthSessionDTO {
	const user = {
		id: MOCK_USER_ID,
		email: "dev@flahasoil.local",
		displayName: "Development User",
		role: UserRole.ADMIN,
		emailVerified: true,
		accountStatus: AccountStatus.ACTIVE,
		createdAt: NOW,
		updatedAt: NOW,
		archivedAt: null,
	};
	const memberships = buildMockMemberships();
	const active =
		memberships.find(
			(m) => m.organizationId === mockActiveOrganizationId
		)?.organization ?? memberships[0]?.organization ?? null;
	return {
		accessToken: MOCK_ACCESS_TOKEN,
		accessTokenExpiresAt: MOCK_ACCESS_TOKEN_EXPIRES_AT,
		user,
		activeOrganization: active ?? null,
		memberships,
	};
}

// Phase 9B — in-memory org admin stores. Mirror the backend's tenant
// isolation: every member/invitation row carries an `organizationId` and
// is filtered by it. The mock seeds both demo orgs with the dev user.
const orgMemberStore = new Map<string, OrganizationMemberDTO[]>();
const orgInvitationStore = new Map<string, OrganizationInvitationDTO[]>();

function seedMockOrgState() {
	const { personal, demo } = buildMockOrganizations();
	orgMemberStore.set(personal.id, [
		{
			id: MOCK_MEMBERSHIP_ID,
			organizationId: personal.id,
			userId: MOCK_USER_ID,
			role: OrganizationRole.OWNER,
			status: MembershipStatus.ACTIVE,
			invitedById: null,
			invitedAt: null,
			acceptedAt: NOW,
			createdAt: NOW,
			updatedAt: NOW,
			organization: personal,
			userEmail: "dev@flahasoil.local",
			userDisplayName: "Development User",
		},
	]);
	orgMemberStore.set(demo.id, [
		{
			id: MOCK_DEMO_MEMBERSHIP_ID,
			organizationId: demo.id,
			userId: MOCK_USER_ID,
			role: OrganizationRole.AGRONOMIST,
			status: MembershipStatus.ACTIVE,
			invitedById: null,
			invitedAt: null,
			acceptedAt: NOW,
			createdAt: NOW,
			updatedAt: NOW,
			organization: demo,
			userEmail: "dev@flahasoil.local",
			userDisplayName: "Development User",
		},
	]);
	orgInvitationStore.set(personal.id, []);
	orgInvitationStore.set(demo.id, []);
}
seedMockOrgState();

function getMockOrgOrThrow(organizationId: string) {
	const { personal, demo } = buildMockOrganizations();
	if (organizationId === personal.id) return personal;
	if (organizationId === demo.id) return demo;
	throw new Error(`Mock organization not found: ${organizationId}`);
}

function nowIso(): string {
	return new Date().toISOString();
}

function nextInvitationId(orgId: string): string {
	const seq = (orgInvitationStore.get(orgId)?.length ?? 0) + 1;
	return `inv_mock_${orgId.slice(-4)}_${seq.toString().padStart(3, "0")}`;
}

// In-memory project store so the wizard's "select project" dropdown and
// the Projects pages stay consistent across navigations within a single
// browser session. Seeded with one fixture so the empty-state can be
// inspected by clearing it.
const projectStore = new Map<string, ProjectDTO>();
projectStore.set(MOCK_PROJECT_ID, {
	id: MOCK_PROJECT_ID,
	userId: MOCK_USER_ID,
	name: "Doha Demo Project",
	code: "DOHA-01",
	description: "Seed project used by the mock client.",
	locationName: "Doha, Qatar",
	status: ProjectStatus.ACTIVE,
	createdAt: NOW,
	updatedAt: NOW,
});

function nextProjectId(): string {
	return `proj_mock_${(projectStore.size + 1).toString().padStart(3, "0")}`;
}

function projectSummary(p: ProjectDTO, sampleCount: number): ProjectSummaryDTO {
	return {
		id: p.id,
		name: p.name,
		code: p.code ?? null,
		status: p.status,
		sampleCount,
		createdAt: p.createdAt,
		updatedAt: p.updatedAt,
	};
}

export const mockApiV2Client: ApiV2Client = {
	async getMe(): Promise<GetCurrentUserResponse> {
		return {
			session: {
				mode: "dev",
				user: {
					id: MOCK_USER_ID,
					email: "dev@flahasoil.local",
					displayName: "Development User",
					role: UserRole.ADMIN,
					emailVerified: true,
		accountStatus: AccountStatus.ACTIVE,
					createdAt: NOW,
					updatedAt: NOW,
					archivedAt: null,
				},
			},
		};
	},

	// ---------------------------------------------------------------
	// Phase 9A-G — Auth (mock)
	//
	// The mock client treats register/login/refresh as no-ops that
	// always succeed and return the same canned session. The real
	// 401/credentials flow is exercised against the backend.
	// ---------------------------------------------------------------
	async register(_body: RegisterRequest): Promise<AuthRegisterResponse> {
		return { session: buildMockAuthSession() };
	},

	async login(_body: LoginRequest): Promise<AuthLoginResponse> {
		return { session: buildMockAuthSession() };
	},

	async refresh(): Promise<AuthRefreshResponse> {
		return { session: buildMockAuthSession() };
	},

	async logout(): Promise<AuthLogoutResponse> {
		return { ok: true };
	},

	async authMe(): Promise<AuthMeResponse> {
		const session = buildMockAuthSession();
		return {
			user: session.user,
			activeOrganization: session.activeOrganization,
			memberships: session.memberships,
		};
	},

	async verifyEmail(_body) {
		const session = buildMockAuthSession();
		return { ok: true as const, user: { ...session.user, emailVerified: true } };
	},

	async resendVerification() {
		return { ok: true as const };
	},

	async forgotPassword(_body) {
		return { ok: true as const };
	},

	async resetPassword(_body) {
		return { ok: true as const };
	},

	// Phase 9A-H — mock tenant listing + switching. The switch updates
	// the module-scoped `mockActiveOrganizationId` so subsequent
	// `refresh` / `authMe` calls reflect the new tenant. Membership
	// existence is verified locally so the mock behaves like the real
	// backend (404 when the org is unknown).
	async listMyOrganizations(): Promise<UserMembershipsResponse> {
		return {
			activeOrganizationId: mockActiveOrganizationId,
			memberships: buildMockMemberships(),
		};
	},

	async changePassword(_body: {
		currentPassword: string;
		newPassword: string;
	}): Promise<{ ok: true }> {
		return { ok: true };
	},

	async createProfessionalCheckout(_body?: {
		successUrl?: string;
		cancelUrl?: string;
	}): Promise<{ url: string; sessionId: string }> {
		return {
			url: "https://checkout.stripe.com/mock",
			sessionId: "cs_mock",
		};
	},

	async createBillingPortal(_body?: {
		returnUrl?: string;
	}): Promise<{ url: string }> {
		return { url: "https://billing.stripe.com/mock" };
	},

	async leaveOrganization(_organizationId: string): Promise<{ ok: true }> {
		return { ok: true };
	},

	async suspendMembership(organizationId: string, userId: string) {
		const members = orgMemberStore.get(organizationId) ?? [];
		const m = members.find((x) => x.userId === userId);
		if (!m) throw new Error("Mock: membership not found");
		m.status = MembershipStatus.SUSPENDED;
		return { member: m };
	},

	async unsuspendMembership(organizationId: string, userId: string) {
		const members = orgMemberStore.get(organizationId) ?? [];
		const m = members.find((x) => x.userId === userId);
		if (!m) throw new Error("Mock: membership not found");
		m.status = MembershipStatus.ACTIVE;
		return { member: m };
	},

	async transferOwnership(
		_organizationId: string,
		body: import("@flaha/shared-types").TransferOwnershipRequest
	) {
		return {
			ok: true as const,
			previousOwnerId: MOCK_USER_ID,
			newOwnerId: body.targetUserId,
		};
	},

	async submitEnterpriseEnquiry(
		_body: import("@flaha/shared-types").EnterpriseEnquiryRequest
	): Promise<import("@flaha/shared-types").EnterpriseEnquiryResponse> {
		return { ok: true, enquiryId: "enq_mock_001" };
	},

	async listEnterpriseEnquiries(): Promise<
		import("@flaha/shared-types").ListEnterpriseEnquiriesResponse
	> {
		return { enquiries: [] };
	},

	async provisionEnterprise(
		_organizationId: string,
		_body: import("@flaha/shared-types").ProvisionEnterpriseRequest
	): Promise<import("@flaha/shared-types").ProvisionEnterpriseResponse> {
		const usage = await this.getMyUsage();
		return {
			subscription: usage.usage.subscription!,
			entitlements: {
				...usage.usage.entitlements,
				tier: "ENTERPRISE" as import("@flaha/shared-types").SubscriptionTier,
				allowAdvanced: true,
				allowCustomReport: true,
				allowCustomBranding: true,
			},
		};
	},

	async getMyUsage(): Promise<import("@flaha/shared-types").GetOrgUsageResponse> {
		return {
			usage: {
				organizationId: mockActiveOrganizationId,
				subscription: {
					id: "sub_mock",
					organizationId: mockActiveOrganizationId,
					tier: "PROFESSIONAL" as import("@flaha/shared-types").SubscriptionTier,
					status: "ACTIVE" as import("@flaha/shared-types").SubscriptionStatus,
					grandfathered: true,
					currentPeriodStart: null,
					currentPeriodEnd: null,
					cancelAtPeriodEnd: false,
					contractReference: null,
					createdAt: NOW,
					updatedAt: NOW,
				},
				entitlements: {
					tier: "PROFESSIONAL" as import("@flaha/shared-types").SubscriptionTier,
					status: "ACTIVE" as import("@flaha/shared-types").SubscriptionStatus,
					grandfathered: true,
					maxLifetimeTests: null,
					maxTestsPerBillingPeriod: null,
					maxOrganizations: 1,
					maxProjects: null,
					maxMembers: 50,
					allowPreliminary: true,
					allowModerate: true,
					allowAdvanced: false,
					allowStandardReport: true,
					allowCustomReport: false,
					allowCustomBranding: false,
					allowOrgInvites: true,
					allowApiAccess: false,
					billingPeriodKey: null,
					currentPeriodEnd: null,
				},
				lifetimeTestsUsed: 0,
				periodTestsUsed: 0,
			},
		};
	},

	async switchOrganization(
		body: SwitchOrganizationRequest
	): Promise<SwitchOrganizationResponse> {
		const exists = buildMockMemberships().some(
			(m) => m.organizationId === body.organizationId
		);
		if (!exists) {
			throw new Error(`Mock: unknown organization ${body.organizationId}`);
		}
		mockActiveOrganizationId = body.organizationId;
		return { session: buildMockAuthSession() };
	},

	// Phase 9B — Organization administration (mock). Operations mutate
	// the module-scoped stores so a single browser session sees a
	// consistent view across navigations.
	async getOrganization(
		organizationId: string
	): Promise<GetOrganizationResponse> {
		return { organization: getMockOrgOrThrow(organizationId) };
	},

	async patchOrganization(
		organizationId: string,
		body: PatchOrganizationRequest
	): Promise<PatchOrganizationResponse> {
		const org = getMockOrgOrThrow(organizationId);
		const updated = {
			...org,
			...(body.name ? { name: body.name } : {}),
			...(body.type ? { type: body.type } : {}),
			updatedAt: nowIso(),
		};
		return { organization: updated };
	},

	async listOrganizationMembers(
		organizationId: string
	): Promise<ListOrganizationMembersResponse> {
		getMockOrgOrThrow(organizationId);
		return { members: orgMemberStore.get(organizationId) ?? [] };
	},

	async patchMembership(
		organizationId: string,
		userId: string,
		body: PatchMembershipRequest
	): Promise<PatchMembershipResponse> {
		const rows = orgMemberStore.get(organizationId) ?? [];
		const idx = rows.findIndex((r) => r.userId === userId);
		if (idx < 0) throw new Error(`Mock membership not found: ${userId}`);
		const next: OrganizationMemberDTO = {
			...rows[idx]!,
			role: body.role,
			updatedAt: nowIso(),
		};
		rows[idx] = next;
		orgMemberStore.set(organizationId, rows);
		return { member: next };
	},

	async removeMembership(
		organizationId: string,
		userId: string
	): Promise<RemoveMembershipResponse> {
		const rows = orgMemberStore.get(organizationId) ?? [];
		const next = rows.filter((r) => r.userId !== userId);
		orgMemberStore.set(organizationId, next);
		return { ok: true };
	},

	async listInvitations(
		organizationId: string
	): Promise<ListInvitationsResponse> {
		getMockOrgOrThrow(organizationId);
		return { invitations: orgInvitationStore.get(organizationId) ?? [] };
	},

	async createInvitation(
		organizationId: string,
		body: CreateInvitationRequest
	): Promise<CreateInvitationResponse> {
		getMockOrgOrThrow(organizationId);
		const invitation: OrganizationInvitationDTO = {
			id: nextInvitationId(organizationId),
			organizationId,
			email: body.email.trim().toLowerCase(),
			role: body.role,
			status: InvitationStatus.PENDING,
			invitedByUserId: MOCK_USER_ID,
			expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
			acceptedAt: null,
			revokedAt: null,
			createdAt: nowIso(),
			updatedAt: nowIso(),
		};
		const rows = orgInvitationStore.get(organizationId) ?? [];
		orgInvitationStore.set(organizationId, [invitation, ...rows]);
		return { invitation };
	},

	async revokeInvitation(
		organizationId: string,
		invitationId: string
	): Promise<RevokeInvitationResponse> {
		const rows = orgInvitationStore.get(organizationId) ?? [];
		const idx = rows.findIndex((r) => r.id === invitationId);
		if (idx < 0) throw new Error(`Mock invitation not found: ${invitationId}`);
		const next: OrganizationInvitationDTO = {
			...rows[idx]!,
			status: InvitationStatus.REVOKED,
			revokedAt: nowIso(),
			updatedAt: nowIso(),
		};
		rows[idx] = next;
		orgInvitationStore.set(organizationId, rows);
		return { invitation: next };
	},

	async acceptInvitation(
		body: AcceptInvitationRequest
	): Promise<AcceptInvitationResponse> {
		// The mock doesn't generate real tokens; treat any non-empty token
		// as accepting the first PENDING invitation in any org so the UI
		// can be exercised end-to-end.
		if (!body.token || body.token.length === 0) {
			throw new Error("Mock: token is required");
		}
		for (const [orgId, rows] of orgInvitationStore) {
			const idx = rows.findIndex((r) => r.status === InvitationStatus.PENDING);
			if (idx >= 0) {
				const inv = rows[idx]!;
				const accepted: OrganizationInvitationDTO = {
					...inv,
					status: InvitationStatus.ACCEPTED,
					acceptedAt: nowIso(),
					updatedAt: nowIso(),
				};
				rows[idx] = accepted;
				orgInvitationStore.set(orgId, rows);
				const org = getMockOrgOrThrow(orgId);
				const membership: OrganizationMembershipDTO = {
					id: `mem_mock_${Date.now()}`,
					organizationId: orgId,
					userId: MOCK_USER_ID,
					role: inv.role,
					status: MembershipStatus.ACTIVE,
					invitedById: inv.invitedByUserId,
					invitedAt: inv.createdAt,
					acceptedAt: accepted.acceptedAt,
					createdAt: nowIso(),
					updatedAt: nowIso(),
					organization: org,
				};
				return { membership, organization: org };
			}
		}
		throw new Error("Mock: no pending invitation to accept");
	},

	async createProject(
		body: CreateProjectRequest
	): Promise<CreateProjectResponse> {
		const id = nextProjectId();
		const project: ProjectDTO = {
			id,
			userId: MOCK_USER_ID,
			name: body.name,
			code: body.code ?? null,
			description: body.description ?? null,
			locationName: body.locationName ?? null,
			status: body.status ?? ProjectStatus.ACTIVE,
			createdAt: NOW,
			updatedAt: NOW,
		};
		projectStore.set(id, project);
		return { project };
	},

	async listProjects(query: ListProjectsQuery): Promise<ListProjectsResponse> {
		const rows = Array.from(projectStore.values()).filter((p) => {
			if (p.userId !== MOCK_USER_ID) return false;
			if (query.status !== undefined && p.status !== query.status) return false;
			return true;
		});
		// One mock sample for the seed project, none for created-in-session.
		return {
			projects: rows.map((p) =>
				projectSummary(p, p.id === MOCK_PROJECT_ID ? 1 : 0)
			),
		};
	},

	async getProjectById(projectId: string): Promise<GetProjectResponse> {
		const project = projectStore.get(projectId);
		if (!project || project.userId !== MOCK_USER_ID) {
			throw new Error(`Mock project not found: ${projectId}`);
		}
		const samples =
			projectId === MOCK_PROJECT_ID
				? [
						{
							id: MOCK_SAMPLE_ID,
							userId: MOCK_USER_ID,
							projectId,
							locationName: "Mock Field A",
							latitude: 25.276987,
							longitude: 51.520008,
							depthFromCm: 0,
							depthToCm: 30,
							sampleDate: NOW,
							createdAt: NOW,
							updatedAt: NOW,
						},
				  ]
				: [];
		return { project, samples };
	},

	async createSoilSample(
		body: CreateSoilSampleRequest
	): Promise<CreateSoilSampleResponse> {
		return {
			sample: {
				id: MOCK_SAMPLE_ID,
				userId: MOCK_USER_ID,
				projectId: body.projectId,
				locationName: body.locationName ?? "Mock Field A",
				latitude: body.latitude ?? 25.276987,
				longitude: body.longitude ?? 51.520008,
				depthFromCm: body.depthFromCm ?? 0,
				depthToCm: body.depthToCm ?? 30,
				sampleDate: body.sampleDate ?? NOW,
				createdAt: NOW,
				updatedAt: NOW,
			},
		};
	},

	async getSoilSample(sampleId: string): Promise<GetSoilSampleResponse> {
		return {
			sample: {
				id: sampleId,
				userId: MOCK_USER_ID,
				projectId: null,
				locationName: "Mock Field A",
				latitude: 25.276987,
				longitude: 51.520008,
				depthFromCm: 0,
				depthToCm: 30,
				sampleDate: NOW,
				createdAt: NOW,
				updatedAt: NOW,
			},
			tests: [
				{
					id: MOCK_TEST_ID,
					testLevel: SoilTestLevel.MODERATE,
					labName: "Mock Lab",
					testDate: NOW,
					hasPhysicsResult: true,
					hasChemistryResult: true,
					hasInterpretation: true,
					createdAt: NOW,
				},
			],
		};
	},

	async createSoilTest(
		body: CreateSoilTestRequest
	): Promise<CreateSoilTestResponse> {
		return {
			soilTest: {
				id: MOCK_TEST_ID,
				sampleId: body.sampleId,
				testLevel: body.testLevel,
				labName: body.labName ?? "Mock Lab",
				labReference: body.labReference ?? null,
				testDate: body.testDate ?? NOW,
				notes: body.notes ?? null,
				createdAt: NOW,
				updatedAt: NOW,
			},
		};
	},

	async getSoilTest(soilTestId: string): Promise<GetSoilTestResponse> {
		return {
			soilTest: {
				id: soilTestId,
				sampleId: MOCK_SAMPLE_ID,
				testLevel: SoilTestLevel.MODERATE,
				labName: "Mock Lab",
				labReference: "MOCK-001",
				testDate: NOW,
				notes: null,
				createdAt: NOW,
				updatedAt: NOW,
			},
			textureInput: {
				id: "txin_mock_001",
				soilTestId,
				sandPercent: 40,
				siltPercent: 40,
				clayPercent: 20,
				organicMatterPercent: 2.5,
				bulkDensity: 1.35,
				gravelPercent: 0,
				source: SoilValueSource.LAB,
				createdAt: NOW,
				updatedAt: NOW,
			},
			physicsResult: mockPhysicsResult(soilTestId),
			chemistryResult: mockChemistryResult(soilTestId),
			interpretation: mockInterpretation(soilTestId),
			reports: [],
		};
	},

	async calculateSoilTest(
		soilTestId: string,
		body: CalculateSoilTestRequest
	): Promise<CalculateSoilTestResponse> {
		const response: CalculateSoilTestResponse = {
			warnings: [],
			warningDetails: [],
		};
		if (body.runPhysics) {
			response.physicsResult = mockPhysicsResult(soilTestId);
		}
		if (body.runChemistry) {
			response.chemistryResult = mockChemistryResult(soilTestId);
		}
		if (body.runInterpretation) {
			response.interpretation = mockInterpretation(soilTestId);
		}
		return response;
	},

	async getSoilInterpretation(
		soilTestId: string
	): Promise<GetSoilInterpretationResponse> {
		return { interpretation: mockInterpretation(soilTestId) };
	},

	async createSoilReport(
		soilTestId: string,
		body: CreateSoilReportRequest
	): Promise<CreateSoilReportResponse> {
		return {
			report: {
				id: "rpt_mock_001",
				soilTestId,
				status: SoilReportStatus.READY,
				title: "Mock report",
				reportNumber: "FLH-MOCK-001",
				archived: false,
				currentVersionId: "rpv_mock_001",
				latestVersionNumber: 1,
				reportType: body.reportType,
				fileUrl: "https://example.invalid/mock-report.pdf",
				generatedAt: NOW,
				createdAt: NOW,
				updatedAt: NOW,
			},
		};
	},

	async getSoilTestReport(
		soilTestId: string
	): Promise<GetSoilTestReportResponse> {
		const full = await this.getSoilTest(soilTestId);
		return {
			sample: {
				id: full.soilTest.sampleId,
				userId: MOCK_USER_ID,
				projectId: null,
				locationName: "Mock Field A",
				latitude: 25.276987,
				longitude: 51.520008,
				depthFromCm: 0,
				depthToCm: 30,
				sampleDate: NOW,
				createdAt: NOW,
				updatedAt: NOW,
			},
			test: full.soilTest,
			physics: full.physicsResult ?? null,
			chemistry: full.chemistryResult ?? null,
			interpretation: full.interpretation ?? null,
			warnings: [],
			warningDetails: [],
			auditTrace: {
				physicsTrace: null,
				chemistryInputsUsed: null,
				skippedModules: [],
			},
			metadata: {
				generatedAt: NOW,
				version: "v2.0.0",
				calculationMode: "LAB",
				testLevel: full.soilTest.testLevel,
			},
		};
	},

	async getSoilTestReportSummary(
		soilTestId: string
	): Promise<GetSoilTestReportSummaryResponse> {
		const full = await this.getSoilTest(soilTestId);
		return {
			soilTestId,
			sampleId: full.soilTest.sampleId,
			testLevel: full.soilTest.testLevel,
			labName: full.soilTest.labName ?? null,
			textureClass: full.physicsResult?.textureClass ?? null,
			overallSoilRating: full.interpretation?.overallSoilRating ?? null,
			phCategory: full.interpretation?.phCategory ?? null,
			salinityRisk: full.interpretation?.salinityRisk ?? null,
			hasChemistry: !!full.chemistryResult,
			hasInterpretation: !!full.interpretation,
			warningCount: 0,
			generatedAt: NOW,
		};
	},

	async getFlahaCalcExport(
		soilTestId: string
	): Promise<FlahaCalcExportResponse> {
		return {
			soilTestId,
			textureClass: "Loam",
			fieldCapacity: 0.28,
			wiltingPoint: 0.12,
			plantAvailableWater: 0.16,
			saturation: 0.45,
			saturatedConductivity: 12.5,
			cec: 18.2,
			salinityRisk: "Low",
			sodiumRisk: "Low",
			warnings: [],
		};
	},

	async getScientificAnalysis(
		soilTestId: string
	): Promise<ScientificAnalysisResponse> {
		// Loam soil (~40 % sand, 40 % silt, 20 % clay) with a balanced
		// Ca/Mg/K profile so the three triangles + retention curve all
		// render against a realistic centroid in mock mode.
		return {
			soilTestId,
			texture: {
				sand: 40,
				silt: 40,
				clay: 20,
				derived: null,
				sumOk: true,
				sumDelta: 0,
				normalized: { sand: 40, silt: 40, clay: 20 },
				point: { x: 0.4, y: Math.sqrt(3) / 2 - 0.2 * (Math.sqrt(3) / 2) },
				classification: "Loam",
				matched: true,
			},
			waterRetention: {
				method: "saxton-rawls-2006",
				textureClass: "Loam",
				points: [
					{ pF: 0, tensionKpa: 0, waterContentVolPercent: 45 },
					{ pF: 2.53, tensionKpa: 33, waterContentVolPercent: 28 },
					{ pF: 4.18, tensionKpa: 1500, waterContentVolPercent: 12 },
				],
				saturation: { pF: 0, tensionKpa: 0, waterContentVolPercent: 45, label: "Saturation" },
				fieldCapacity: { pF: 2.53, tensionKpa: 33, waterContentVolPercent: 28, label: "FC" },
				wiltingPoint: { pF: 4.18, tensionKpa: 1500, waterContentVolPercent: 12, label: "WP" },
				irrigationThreshold: { pF: 2.9, tensionKpa: 80, waterContentVolPercent: 20, label: "MAD 50%" },
				plantAvailableWater: 16,
				madFraction: 0.5,
				airEntryTensionKpa: 1.5,
				parameterA: 1000,
				parameterB: -4.5,
				// Phase 10A.7 (WS1 / WS2) — units + bulk-density traceability.
				units: {
					waterContent: "% v/v",
					tension: "kPa",
					plantAvailableWater: "% v/v",
				},
				bulkDensity: {
					predicted: 1.45,
					used: 1.4,
					source: "USER_INPUT",
					unit: "g/cm³",
				},
			},
			structure: {
				ca: 12, mg: 3, k: 0.6, na: 0.2, cec: 18.2,
				normalized: { ca: 76.9, mg: 19.2, k: 3.85 },
				point: { x: 50, y: 30 },
				classification: "Balanced",
				matched: true,
				caMgRatio: 4, caKRatio: 20, mgKRatio: 5,
				basesTotal: 15.6,
				// Phase 10A.7 (WS5 — R3) — unit + Bear/Albrecht disclaimer.
				unit: "cmol(+)/kg",
				disclaimer:
					"Bear/Albrecht (BCSR) cation balance is a diagnostic visualisation only.",
			},
			warnings: [],
		};
	},

	// Phase 8D — Report management surface (mock).
	async generateReport(
		soilTestId: string,
		body: GenerateReportRequest
	): Promise<GenerateReportResponse> {
		const report = mockSoilReport(soilTestId, body);
		const version = mockReportVersion(report.id, 1);
		return { report, version };
	},

	async listProjectReports(
		projectId: string
	): Promise<ListProjectReportsResponse> {
		void projectId;
		return { reports: [mockSoilReport(MOCK_TEST_ID, {})] };
	},

	async getReport(reportId: string): Promise<GetReportResponse> {
		const report = mockSoilReport(MOCK_TEST_ID, {}, reportId);
		const v1 = mockReportVersion(reportId, 1);
		return {
			report,
			versions: [
				{
					id: v1.id,
					reportId: v1.reportId,
					versionNumber: v1.versionNumber,
					status: v1.status,
					overallSoilRating: v1.overallSoilRating ?? null,
					textureClass: v1.textureClass ?? null,
					generatedAt: v1.generatedAt,
					generatedByUserId: v1.generatedByUserId ?? null,
				},
			],
			currentVersion: v1,
		};
	},

	async listReportVersions(
		reportId: string
	): Promise<ListReportVersionsResponse> {
		const v1 = mockReportVersion(reportId, 1);
		return {
			versions: [
				{
					id: v1.id,
					reportId: v1.reportId,
					versionNumber: v1.versionNumber,
					status: v1.status,
					overallSoilRating: v1.overallSoilRating ?? null,
					textureClass: v1.textureClass ?? null,
					generatedAt: v1.generatedAt,
					generatedByUserId: v1.generatedByUserId ?? null,
				},
			],
		};
	},

	async getReportVersion(
		reportId: string,
		versionNumber: number
	): Promise<GetReportVersionResponse> {
		return { version: mockReportVersion(reportId, versionNumber) };
	},

	async regenerateReport(reportId: string): Promise<RegenerateReportResponse> {
		const report = mockSoilReport(MOCK_TEST_ID, {}, reportId);
		const version = mockReportVersion(reportId, 2);
		return { report, version };
	},

	async patchReport(
		reportId: string,
		body: PatchReportRequest
	): Promise<PatchReportResponse> {
		const report = mockSoilReport(MOCK_TEST_ID, {}, reportId);
		if (body.title) report.title = body.title;
		if (typeof body.archived === "boolean") report.archived = body.archived;
		return { report };
	},

	getReportVersionPreviewUrl(reportId: string, versionNumber: number): string {
		return `mock://reports/${reportId}/versions/${versionNumber}/preview`;
	},

	async getReportVersionPdfBlob(
		reportId: string,
		versionNumber: number,
		chartSnapshots?: ChartSnapshotsDTO
	): Promise<Blob> {
		void reportId;
		void versionNumber;
		void chartSnapshots;
		// Minimal valid PDF payload so the download flow can be exercised
		// offline without a backend.
		return new Blob(["%PDF-1.4\n%mock FlahaSOIL report\n"], {
			type: "application/pdf",
		});
	},
};

// ---------------------------------------------------------------------------
// Internal fixtures
// ---------------------------------------------------------------------------

function mockPhysicsResult(soilTestId: string) {
	return {
		id: "phys_mock_001",
		soilTestId,
		fieldCapacity: 0.28,
		wiltingPoint: 0.12,
		plantAvailableWater: 0.16,
		saturation: 0.45,
		saturatedConductivity: 12.5,
		textureClass: "Loam",
		bulkDensity: 1.35,
		porosity: 0.49,
		voidRatio: 0.96,
		particleDensity: 2.65,
		soilQualityIndex: null,
		drainageClass: "Well drained",
		compactionRisk: "Low",
		erosionRisk: "Low",
		calculationVersion: "saxton-rawls-2006",
		calculationTraceJson: null,
		createdAt: NOW,
		updatedAt: NOW,
	};
}

function mockChemistryResult(soilTestId: string) {
	return {
		id: "chem_mock_001",
		soilTestId,
		cec: 18.2,
		baseSaturation: 78.5,
		caPercent: 60.0,
		mgPercent: 12.5,
		kPercent: 4.0,
		naPercent: 2.0,
		esp: 2.0,
		sar: 1.5,
		cationBalanceOther: 21.5,
		calculationMode: "LAB",
		createdAt: NOW,
		updatedAt: NOW,
	};
}

function mockInterpretation(soilTestId: string) {
	return {
		id: "intp_mock_001",
		soilTestId,
		phCategory: "Slightly alkaline",
		salinityRisk: "Low",
		cecLevel: "Moderate",
		baseSaturationCategory: "Adequate",
		cationBalance: "Balanced",
		sodiumRisk: "Low",
		waterHoldingClass: "Moderate",
		drainageClass: "Well drained",
		overallSoilRating: SoilInterpretationRating.GOOD,
		warningsJson: [],
		createdAt: NOW,
		updatedAt: NOW,
	};
}


function mockSoilReport(
	soilTestId: string,
	body: GenerateReportRequest,
	reportId = "rpt_mock_001"
): SoilReportDTO {
	return {
		id: reportId,
		soilTestId,
		status: SoilReportStatus.READY,
		title: body.title ?? "Doha Demo report",
		reportNumber: body.reportNumber ?? "FLH-MOCK-001",
		archived: false,
		currentVersionId: "rpv_mock_001",
		latestVersionNumber: 1,
		reportType: "PROFESSIONAL_V1",
		fileUrl: null,
		generatedAt: NOW,
		createdAt: NOW,
		updatedAt: NOW,
	};
}

function mockProfessionalReport(): ProfessionalReportDTO {
	return {
		schemaVersion: "1.0",
		cover: {
			projectName: "Doha Demo Project",
			projectCode: "DOHA-01",
			clientName: "Demo Client",
			consultantName: "Development User",
			consultantRole: "Senior Agronomist",
			location: "Doha, Qatar",
			latitude: 25.276987,
			longitude: 51.520008,
			sampleId: MOCK_SAMPLE_ID,
			sampleCode: null,
			reportNumber: "FLH-MOCK-001",
			reportTitle: "Doha Demo report",
			reportDate: NOW,
			testLevel: "MODERATE",
		},
		executiveSummary: {
			overallRating: "Good",
			headlineFindings: [
				"USDA texture class: Loam — well suited to most crops.",
				"Plant-available water 16% — good water holding capacity.",
				"Salinity low (ECe < 2 dS/m) — no leaching required.",
			],
			actionItemCount: 1,
		},
		texture: { usdaClass: "Loam", sandPercent: 40, siltPercent: 40, clayPercent: 20, organicMatterPercent: 2.5 },
		physics: {
			fieldCapacity: 0.28, wiltingPoint: 0.12, plantAvailableWater: 0.16,
			bulkDensity: 1.35, porosity: 0.49, saturation: 0.45, saturatedConductivity: 12.5,
			units: { moisture: "% v/v", bulkDensity: "g/cm³", conductivity: "mm/h", porosity: "% v/v", saturation: "% v/v" },
		},
		chemistry: {
			pH: 7.6, ece: 1.2, organicMatter: 2.5, cec: 18.2,
			macroNutrients: { n: 25, p: 18, k: 220 },
			secondaryNutrients: { ca: 1500, mg: 220, s: 15 },
			micronutrients: { fe: 8, mn: 12, zn: 2, cu: 1.5, b: 0.6 },
			calculationMode: "LAB",
		},
		salinity: { severity: "Slight", riskLabel: "Low", ece: 1.2, recommendation: "No action required." },
		sodicity: { severity: "None", riskLabel: "Low", sar: 1.5, esp: 2.0, recommendation: "No action required." },
		irrigation: { infiltrationClass: "Moderate", drainageClass: "Well drained", waterHoldingClass: "Moderate", leachingRequirement: 0.05, notes: [] },
		agronomic: {
			overallSoilRating: "Good",
			categories: [
				{ label: "pH", value: "7.6", status: "good" },
				{ label: "Salinity", value: "1.2 dS/m", status: "good" },
				{ label: "CEC", value: "18.2 cmol+/kg", status: "good" },
			],
			suitability: {
				turfgrass: { verdict: "Suitable", reasons: ["Loam texture, balanced chemistry."] },
				agriculture: { verdict: "Suitable", reasons: ["Adequate water holding capacity."] },
			},
		},
		recommendations: {
			short: [{ code: "REC-MON-001", severity: "INFO", horizon: "SHORT", category: "MONITORING", title: "Monitor pH quarterly", body: "Track soil pH over the season." }],
			medium: [], long: [],
		},
		notes: { missingValues: [], estimatedValues: [], calculationWarnings: [] },
		appendix: { calculationSummary: "Saxton & Rawls 2006 + chemistry pipeline.", inputs: {} },
		source: {} as never,
	};
}

function mockReportVersion(reportId: string, versionNumber: number): ReportVersionDTO {
	return {
		id: `rpv_mock_${versionNumber.toString().padStart(3, "0")}`,
		reportId,
		versionNumber,
		status: SoilReportStatus.READY,
		generatedByUserId: MOCK_USER_ID,
		overallSoilRating: "Good",
		textureClass: "Loam",
		errorMessage: null,
		generatedAt: NOW,
		createdAt: NOW,
		snapshot: mockProfessionalReport(),
	};
}
